import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './PaymentSuccess.css';
import PaymentStateScreen from './PaymentStateScreen';
import {
  ConfirmationPage, ConfirmationHero, ConfirmationPanel, ConfirmationCallout, ConfirmationSteps,
  ConfirmationContacts, ConfirmationFooter
} from './PaymentConfirmation';

// Create a separate axios instance for public routes
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
});

const PaymentSuccess = () => {
  const { confirmationNumber } = useParams();
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConfirmationDetails();
  }, [confirmationNumber]);

  const fetchConfirmationDetails = async () => {
    try {
      const response = await publicAxios.get(`/public/payment-confirmation/${confirmationNumber}`);
      setConfirmationDetails(response.data);
      setLoading(false);
    } catch (err) {
      setError('Unable to load confirmation details');
      setLoading(false);
    }
  };

  if (loading) {
    return <PaymentStateScreen variant="loading" message="Loading your confirmation..." />;
  }

  if (error) {
    return (
      <PaymentStateScreen
        variant="error"
        title="We Couldn't Load Your Confirmation"
        message={error}
        hint={`Confirmation number: ${confirmationNumber}. Please try again, or contact your travel agent.`}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const remainingBalance = confirmationDetails && confirmationDetails.paymentType === 'deposit' 
    ? (parseFloat(confirmationDetails.totalAmount) - parseFloat(confirmationDetails.amount)).toFixed(2)
    : null;

  const money = (value) => parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <ConfirmationPage>
      <ConfirmationHero title="Payment Information Received">
        <p>Your payment details have been securely submitted to your travel agent.</p>
      </ConfirmationHero>

      <ConfirmationPanel
        title="Confirmation Details"
        rows={[
          { label: 'Confirmation Number', value: confirmationNumber, tone: 'mono' },
          ...(confirmationDetails ? [
            { label: 'Quote Number', value: confirmationDetails.quoteNumber },
            { label: 'Trip', value: confirmationDetails.quoteTitle },
            { label: 'Payment Type', value: confirmationDetails.paymentType === 'deposit' ? 'Deposit' : 'Full Payment' },
            { label: 'Amount to be Paid', value: `$${money(confirmationDetails.amount)}`, tone: 'accent' },
            remainingBalance && { label: 'Remaining Balance', value: `$${money(remainingBalance)}`, balance: true },
            { label: 'Submission Date', value: new Date(confirmationDetails.paymentDate).toLocaleDateString() }
          ] : [])
        ]}
      />

      <ConfirmationCallout title="Important Payment Information">
        <ConfirmationSteps
          items={[
            { icon: 'card', content: (<><strong>Your card has NOT been charged yet.</strong> Your payment information has been securely submitted to your travel agent.</>) },
            { icon: 'clock', content: (<><strong>Agent Notification:</strong> Your travel agent has been notified that you're ready to process your payment.</>) },
            { icon: 'building', content: (<><strong>Charge Source:</strong> When processed, the charge will appear on your statement from the travel supplier (hotel, airline, etc.), not from your travel agent.</>) }
          ]}
        />
      </ConfirmationCallout>

      <ConfirmationPanel title="What's Next?">
        <ConfirmationSteps
          items={[
            { icon: 'mail', content: 'A confirmation email has been sent to your email address' },
            { icon: 'phone', content: 'Your travel agent will contact you within 24-48 hours' },
            { icon: 'card', content: 'Your travel agent will process the requested payment on your behalf to the supplier' },
            remainingBalance && { icon: 'clock', content: `Your remaining balance of $${money(remainingBalance)} will be due before your travel date` },
            { icon: 'plane', content: 'Start preparing for your amazing trip!' }
          ]}
        />
      </ConfirmationPanel>

      {confirmationDetails && (
        <ConfirmationPanel title="Your Travel Agent">
          <p className="pc-agent-name">{confirmationDetails.agentName}</p>
          {confirmationDetails.agencyName && <p className="pc-agent-agency">{confirmationDetails.agencyName}</p>}
          <ConfirmationContacts
            items={[
              { icon: 'phone', value: confirmationDetails.agencyPhone, href: confirmationDetails.agencyPhone ? `tel:${confirmationDetails.agencyPhone}` : undefined },
              { icon: 'mail', value: confirmationDetails.agencyEmail, href: confirmationDetails.agencyEmail ? `mailto:${confirmationDetails.agencyEmail}` : undefined }
            ]}
          />
        </ConfirmationPanel>
      )}

      <div className="pc-actions">
        <button type="button" className="pc-btn is-secondary" onClick={() => window.print()}>
          Print Confirmation
        </button>
        <Link to="/" className="pc-btn">
          Return to Home
        </Link>
      </div>

      <ConfirmationFooter>
        <p>Need help? Contact us at <a href="mailto:support@tpcrm.io">support@tpcrm.io</a></p>
      </ConfirmationFooter>
    </ConfirmationPage>
  );
};

export default PaymentSuccess;
